// ============================================================
// ELC Shift — Uploads / Historical Sales (one-time setup) module
//
// Reads the "Last Year by Store" sheet from an xlsx. Uses the
// "Date LY" column (actual prior-year date) as the storage key —
// NOT "Date TY" (this year). Writes rows with date in the prior
// year so they're indistinguishable from regular sales rows once
// stored. One-time setup feature: run once per store to bootstrap
// the prior-year sales data needed for "vs LY" KPI comparisons.
//
// Public surface (callable from admin.html inline handlers):
//   Uploads.historical.handleDrop(event)        — file dropzone drop
//   Uploads.historical.handleFile(file)         — programmatic file input
//   Uploads.historical.acceptSuggestion(...)    — "Did you mean...?" accept
//   Uploads.historical.dismissSuggestion(store) — "Did you mean...?" skip
//   Uploads.historical.save()                   — upsert to DB.sales
//   Uploads.historical.clearPreview()           — wipe preview UI
//
// Free globals (referenced as bare identifiers — they live as
//   lexical globals in admin.html, NOT as window properties.
//   Top-level const/let don't attach to window; bare references
//   resolve via the global lexical scope at runtime):
//   - XLSX                          (SheetJS)
//   - buildUploadNameMap            (admin.html helper)
//   - escapeHtml                    (admin.html escape utility)
//   - ALL_STORES                    (admin.html canonical store list)
//   - DB                            (db.js facade — DB.sales.upload)
//   - showToast                     (admin.html toast helper)
//   - showHistoricalUploadStatus    (admin.html status display, Section F)
//
// Same architecture as src/uploads/{budget,daily,addon}.js.
// Build via build.js.
// ============================================================

import { ensureXlsx } from '../shared/ensure-xlsx.js';   // v136.8: the spreadsheet library loads only when it is used
import {
  parseMatrixSheet,
  findStoreColumnSuggestions,
  renderStoreSuggestionCards,
} from './shared.js';

// Module state — fresh on every parsed file. Encapsulated so
// admin.html callers go through the public surface only.
let historicalParseState = null;
let parsedHistorical     = null;

export function handleDrop(e) {
  e.preventDefault();
  e.currentTarget.style.borderColor = 'var(--border)';
  e.currentTarget.style.background = 'var(--surface-2)';
  if (e.dataTransfer.files.length) handleFile(e.dataTransfer.files[0]);
}

export function handleFile(file) {
  if (!file || !file.name.endsWith('.xlsx')) {
    showHistoricalUploadStatus('Please upload an .xlsx file', 'error'); return;
  }
  // New file → fresh state.
  historicalParseState = null;
  showHistoricalUploadStatus('Reading ' + file.name + '...', 'info');
  // v136.8: the spreadsheet library loads on first use; this runs again once it has (or says why it could not).
  if (typeof XLSX === 'undefined') { ensureXlsx().then(function () { handleFile(file); }, function (e) { showHistoricalUploadStatus(e.message, 'error'); }); return; }
  const reader = new FileReader();
  reader.onload = function(e) {
    try {
      const wb = XLSX.read(e.target.result, {type:'array', cellDates:true});
      parseSheet(wb, file.name);
    } catch(err) {
      showHistoricalUploadStatus('Error reading file: ' + err.message, 'error');
      console.error('[historical-upload]', err);
    }
  };
  reader.readAsArrayBuffer(file);
}

function parseSheet(wb, filename) {
  const sheetByName = {};
  wb.SheetNames.forEach(n => { sheetByName[n.toLowerCase()] = n; });
  const lySheetName = sheetByName['last year by store'];

  if (!lySheetName) {
    showHistoricalUploadStatus('Could not find "Last Year by Store" sheet in this file. Sheets found: ' + wb.SheetNames.join(', '), 'error');
    return;
  }

  const dynamicMap = buildUploadNameMap();
  const storeMapUpper = {};
  Object.entries(dynamicMap).forEach(([k,v]) => { storeMapUpper[k.trim().toUpperCase()] = v; });

  // Critical: use "Date LY" (the actual past-year date) — NOT "Date TY"
  // (which is the mapping to this year). We're storing actual historical
  // facts, so the date is the date the sale happened.
  const lyRows = XLSX.utils.sheet_to_json(wb.Sheets[lySheetName], {header:1, raw:false, dateNF:'yyyy-mm-dd'});

  historicalParseState = {
    rows: lyRows,
    storeMapUpper,
    accepted: [],
    dismissed: new Set(),
  };

  rebuildFromState();
}

function rebuildFromState() {
  if (!historicalParseState) return;
  const { rows, storeMapUpper, accepted, dismissed } = historicalParseState;
  const parsed = parseMatrixSheet(rows, storeMapUpper, 'Date LY', accepted);
  if (!parsed.ok) { showHistoricalUploadStatus(parsed.error, 'error'); return; }

  const flat = [];
  Object.entries(parsed.byStoreDate).forEach(([store, dates]) => {
    Object.entries(dates).forEach(([date, value]) => {
      flat.push({ store, date, sales: value });
    });
  });
  parsedHistorical = flat;

  const storeCount = new Set(flat.map(r => r.store)).size;
  const expectedStores = (typeof ALL_STORES !== 'undefined' && Array.isArray(ALL_STORES))
    ? ALL_STORES.filter(s => s && s !== 'Retail Relief Managers')
    : [];
  const matchedSet = new Set(flat.map(r => r.store));
  const missingStores = expectedStores.filter(s => !matchedSet.has(s));
  const suggestions = findStoreColumnSuggestions(parsed.unmatchedHeaders, missingStores, dismissed);

  const years = [...new Set(flat.map(r => r.date.slice(0,4)))].sort();
  const yearLabel = years.length === 1 ? years[0] : (years[0] + '–' + years[years.length-1]);

  let statusMsg = `✅ Parsed ${flat.length} historical rows for ${yearLabel} across ${storeCount} stores. Review and save.`;
  let statusType = missingStores.length === 0 ? 'success' : 'info';
  if (missingStores.length > 0 && expectedStores.length > 0) {
    statusMsg = `⚠️ Parsed ${storeCount} of ${expectedStores.length} expected stores.${suggestions.length ? ' See suggestions below.' : ''}`;
  }
  showHistoricalUploadStatus(statusMsg, statusType);

  const previewCard = document.getElementById('historicalPreviewCard');
  if (previewCard) previewCard.style.display = 'block';
  const previewEl = document.getElementById('historicalPreview');
  if (!previewEl) return;

  const sample = flat.slice(0, 8);
  let html = '<div style="font-size:12px;color:var(--sub);margin-bottom:8px">' + flat.length + ' rows for year ' + escapeHtml(yearLabel) + '. Sample (first 8):</div>';

  // Suggestion cards — fn names point at the module's public surface.
  html += renderStoreSuggestionCards(suggestions, 'Uploads.historical.acceptSuggestion', 'Uploads.historical.dismissSuggestion');

  // Stores still missing AND with no suggestion
  const suggestedStores = new Set(suggestions.map(s => s.store));
  const orphanMissing = missingStores.filter(s => !suggestedStores.has(s));
  if (orphanMissing.length > 0 && expectedStores.length > 0) {
    html += '<div style="background:var(--adm-warn-pale);border:1px solid var(--adm-warn-line);color:var(--warn-deep);padding:8px 10px;border-radius:6px;margin-bottom:8px;font-size:12px"><strong>⚠️ ' + orphanMissing.length + ' missing — no close match in file:</strong> ' + orphanMissing.slice(0,3).map(escapeHtml).join(', ') + (orphanMissing.length > 3 ? '…' : '') + '</div>';
  }

  html += '<table style="width:100%;font-size:12px;border-collapse:collapse">';
  html += '<thead><tr><th style="text-align:start;padding:4px 8px;border-bottom:1px solid var(--adm-purple-line)">Store</th><th style="text-align:start;padding:4px 8px;border-bottom:1px solid var(--adm-purple-line)">Date</th><th style="text-align:end;padding:4px 8px;border-bottom:1px solid var(--adm-purple-line)">Sales</th></tr></thead><tbody>';
  sample.forEach(r => {
    html += '<tr>';
    html += '<td style="padding:4px 8px">' + escapeHtml(r.store) + '</td>';
    html += '<td style="padding:4px 8px">' + escapeHtml(r.date) + '</td>';
    html += '<td style="text-align:end;padding:4px 8px;font-variant-numeric:tabular-nums">' + (r.sales != null ? Math.round(r.sales).toLocaleString() : '—') + '</td>';
    html += '</tr>';
  });
  html += '</tbody></table>';
  previewEl.innerHTML = html;
}

export function acceptSuggestion(store, colIdx, headerLabel) {
  if (!historicalParseState) return;
  historicalParseState.accepted = historicalParseState.accepted.filter(e => e.appName !== store);
  historicalParseState.accepted.push({ idx: colIdx, appName: store });
  rebuildFromState();
  showToast('Mapped "' + headerLabel + '" → ' + store, 'success');
}

export function dismissSuggestion(store) {
  if (!historicalParseState) return;
  historicalParseState.dismissed.add(store);
  rebuildFromState();
}

export async function save() {
  if (!parsedHistorical || !parsedHistorical.length) {
    showHistoricalUploadStatus('No parsed data to save', 'error'); return;
  }

  showHistoricalUploadStatus('⏳ Uploading ' + parsedHistorical.length + ' historical rows to database…', 'info');
  try {
    const result = await DB.sales.upload(parsedHistorical);
    showHistoricalUploadStatus('✅ Saved ' + (result.inserted || parsedHistorical.length) + ' historical rows. The KPI tab now has prior-year data for "vs LY" comparisons.', 'success');
    showToast('Historical sales uploaded — ' + (result.inserted || parsedHistorical.length) + ' rows', 'success');
    parsedHistorical = null;
    const previewCard = document.getElementById('historicalPreviewCard');
    if (previewCard) previewCard.style.display = 'none';
  } catch(e) {
    console.warn('[DB] Historical upload failed:', e.message);
    showHistoricalUploadStatus('❌ Upload failed: ' + e.message, 'error');
    showToast('Historical upload failed — see console', 'error');
  }
}

export function clearPreview() {
  parsedHistorical = null;
  const previewCard = document.getElementById('historicalPreviewCard');
  if (previewCard) previewCard.style.display = 'none';
  const fileInput = document.getElementById('historicalFileInput');
  if (fileInput) fileInput.value = '';
  showHistoricalUploadStatus('Cancelled. No data uploaded.', 'info');
}
